/**
 * The Phase 5A operations: a pull request's AI review, requesting a run,
 * reviewer recommendations, finding triage, exceptions and the repository's
 * review policy.
 *
 * Split from `operations.ts` for length and re-exported from it, as the
 * other phases are. One function per `/v1` operation, paths checked against
 * the generated contract.
 */

import { expect, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

export type AIReviewOverview = Schemas['AIReviewOverview'];
export type AIReviewRun = Schemas['AIReviewRun'];
export type AIReviewFinding = Schemas['AIReviewFinding'];
export type AIReviewFindingView = Schemas['AIReviewFindingView'];
export type AIReviewEvidence = Schemas['AIReviewEvidence'];
export type AIReviewCategory = Schemas['AIReviewCategory'];
export type AIReviewSeverity = Schemas['AIReviewSeverity'];
export type AIReviewMode = Schemas['AIReviewMode'];
export type AIReviewPolicy = Schemas['AIReviewPolicy'];
export type AIReviewPolicyUpdate = Schemas['AIReviewPolicyUpdate'];
export type AIReviewException = Schemas['AIReviewException'];
export type AIReviewerRecommendations = Schemas['AIReviewerRecommendations'];
export type AIFindingFeedback = Schemas['AIFindingFeedback'];
export type AIFindingFeedbackKind = Schemas['AIFindingFeedbackCreate']['kind'];

interface RepoScope {
  orgId: string;
  repoId: string;
}

const pullPath = ({ orgId, repoId }: RepoScope, number: number) => ({
  org_id: orgId,
  repo_id: repoId,
  number,
});

export function getPullRequestAIReview(
  scope: RepoScope,
  number: number,
): Promise<AIReviewOverview> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/ai-review', {
      params: { path: pullPath(scope, number) },
    }),
  );
}

export function requestPullRequestAIReview(scope: RepoScope, number: number): Promise<AIReviewRun> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/ai-review/runs',
      { params: { path: pullPath(scope, number) } },
    ),
  );
}

export function getPullRequestAIReviewerRecommendations(
  scope: RepoScope,
  number: number,
): Promise<AIReviewerRecommendations> {
  return expect(
    http.GET(
      '/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/ai-review/reviewers',
      { params: { path: pullPath(scope, number) } },
    ),
  );
}

export function dismissPullRequestAIFinding(
  scope: RepoScope,
  number: number,
  findingId: string,
  body: { reason: string },
): Promise<AIReviewFinding> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/ai-review/findings/{finding_id}/dismiss',
      { params: { path: { ...pullPath(scope, number), finding_id: findingId } }, body },
    ),
  );
}

export function recordPullRequestAIFindingFeedback(
  scope: RepoScope,
  number: number,
  findingId: string,
  body: { kind: AIFindingFeedbackKind; note?: string },
): Promise<AIFindingFeedback> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/ai-review/findings/{finding_id}/feedback',
      { params: { path: { ...pullPath(scope, number), finding_id: findingId } }, body },
    ),
  );
}

export function grantPullRequestAIReviewException(
  scope: RepoScope,
  number: number,
  body: { finding_id: string; reason: string; expires_at?: string | null },
): Promise<AIReviewException> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/ai-review/exceptions',
      { params: { path: pullPath(scope, number) }, body },
    ),
  );
}

export function revokePullRequestAIReviewException(
  scope: RepoScope,
  number: number,
  exceptionId: string,
  body?: { reason?: string },
): Promise<AIReviewException> {
  return expect(
    http.DELETE(
      '/organizations/{org_id}/repositories/{repo_id}/pull-requests/{number}/ai-review/exceptions/{exception_id}',
      { params: { path: { ...pullPath(scope, number), exception_id: exceptionId } }, body },
    ),
  );
}

export function getRepositoryAIReviewPolicy(scope: RepoScope): Promise<AIReviewPolicy> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/ai-review-policy', {
      params: { path: { org_id: scope.orgId, repo_id: scope.repoId } },
    }),
  );
}

export function setRepositoryAIReviewPolicy(
  scope: RepoScope,
  body: AIReviewPolicyUpdate,
): Promise<AIReviewPolicy> {
  return expect(
    http.PUT('/organizations/{org_id}/repositories/{repo_id}/ai-review-policy', {
      params: { path: { org_id: scope.orgId, repo_id: scope.repoId } },
      body,
    }),
  );
}
